import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, apiError } from "../adminApi";
import { Panel, Stat, inputClass, buttonClass } from "../components/Bits";
import { HarvestShell, useHarvestHeader } from "./HarvestShell";

const WEEKLY_FIELDS = [
  ["sends", "Sends"], ["delivered", "Delivered"], ["replies", "Replies"],
  ["positive", "Positive replies"], ["bounces", "Bounces"], ["complaints", "Spam complaints"],
  ["meetings", "Meetings"], ["proposals", "Proposals"], ["won", "Groups won"],
  ["credits", "Apollo credits used"],
];

const blank = () => ({
  date: new Date().toISOString().slice(0, 10),
  ...Object.fromEntries(WEEKLY_FIELDS.map(([k]) => [k, 0])),
  spend: {},
});

export default function AdminHarvestKpis() {
  const { settings, pending } = useHarvestHeader();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(blank);
  const [peoOnly, setPeoOnly] = useState(false);
  const [spendLine, setSpendLine] = useState({ name: "", amount: "" });

  const load = () => adminApi.get("/crm/harvest/kpis")
    .then(({ data: body }) => setData(body))
    .catch((error) => toast.error(apiError(error)));

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const spend = spendLine.name && spendLine.amount
        ? { ...form.spend, [spendLine.name]: Number(spendLine.amount) }
        : form.spend;
      await adminApi.post("/crm/harvest/kpis", { ...form, spend });
      toast.success("Week saved");
      setForm(blank());
      setSpendLine({ name: "", amount: "" });
      load();
    } catch (error) { toast.error(apiError(error)); }
  };

  const targets = data?.settings?.targets || {};
  const proforma = data?.settings?.proforma || {};
  const derived = data?.derived || {};
  const totals = data?.totals || {};
  const peo = proforma.peo_only || {};

  const cards = [
    ["Reply rate", `${derived.reply_pct ?? 0}%`, `target ${targets.reply_pct}%+`],
    ["Positive replies", `${derived.positive_pct ?? 0}%`,
      `target ${targets.positive_pct_low}–${targets.positive_pct_high}% of contacts`],
    ["Bounce rate", `${derived.bounce_pct ?? 0}%`, `target under ${targets.bounce_pct}%`],
    ["Spam complaints", `${derived.spam_pct ?? 0}%`, `target under ${targets.spam_pct}%`],
    ["Meetings", totals.meetings ?? 0, `target ${targets.meetings_low}–${targets.meetings_high}`],
    ["Renewal months captured", derived.renewal_months_captured ?? 0,
      `target ${targets.renewal_months}+`],
    ["Proposals", totals.proposals ?? 0,
      `target ${targets.proposals_low}–${targets.proposals_high}`],
    ["QR scans", totals.scans ?? 0, "automatic from /h/ links"],
    ["Cost per meeting", derived.cost_per_meeting != null ? `$${derived.cost_per_meeting}` : "—",
      `spend $${derived.spend_total ?? 0} to date`],
    ["CAC per closed group", derived.cac_per_group != null ? `$${derived.cac_per_group}` : "—",
      `${totals.won ?? 0} won so far`],
    ["Apollo credits", `${totals.credits ?? 0} / ${data?.settings?.apollo_credit_budget ?? 0}`,
      "logged weekly by hand"],
    ["Shipment cost", `$${totals.shipment_cost ?? 0}`, "from the fulfillment board"],
  ];

  return (
    <HarvestShell settings={settings} pending={pending}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="harvest-kpi-cards">
        {cards.map(([label, value, hint]) => (
          <Stat key={label} label={label} value={value} hint={hint}
            testid={`harvest-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
        ))}
      </div>

      <Panel
        title="Pro forma"
        testid="harvest-proforma"
        action={
          <button type="button" onClick={() => setPeoOnly((v) => !v)}
            data-testid="harvest-peo-toggle"
            className={`rounded-full px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
              peoOnly ? "bg-gold/20 text-gold" : "border border-cream/15 text-cream/60"
            }`}>
            {peoOnly ? "PEO-only view" : "Full season view"}
          </button>
        }
      >
        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="harvest-proforma-grid">
          {(peoOnly
            ? [["Expected clients", peo.clients], ["Year 1", `$${(peo.y1 || 0).toLocaleString()}`],
               ["Three-year", `$${(peo.y3 || 0).toLocaleString()}`],
               ["Meetings", proforma.meetings], ["Close rate", `${(proforma.close || 0) * 100}%`],
               ["Payback", proforma.payback]]
            : [["Meetings", proforma.meetings],
               ["Meeting → proposal", `${(proforma.prop || 0) * 100}%`],
               ["Close rate", `${(proforma.close || 0) * 100}%`],
               ["Retention", `${(proforma.ret || 0) * 100}%`],
               ["Expected groups", proforma.groups],
               ["Season cost", `$${(proforma.cost || 0).toLocaleString()}`]]
          ).map(([label, value]) => (
            <div key={label} className="rounded-lg border border-cream/10 px-4 py-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">{label}</p>
              <p className="mt-1.5 font-serif text-xl text-cream">{value ?? "—"}</p>
            </div>
          ))}
        </div>
        {!peoOnly ? (
          <p className="mt-4 text-xs text-cream/45">
            Year 1 ${(proforma.y1 || 0).toLocaleString()} · three-year ${(proforma.y3 || 0).toLocaleString()} ·
            payback {proforma.payback} · season cost includes the Harvest Table breakfast.
          </p>
        ) : (
          <p className="mt-4 text-xs text-cream/45">
            PEO-only: {peo.clients} clients, year 1 ${(peo.y1 || 0).toLocaleString()}, three-year
            ${(peo.y3 || 0).toLocaleString()}.
          </p>
        )}
      </Panel>

      <Panel title="Log a week" testid="harvest-kpi-form">
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">Week of</span>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="harvest-kpi-date" className={`${inputClass} mt-1.5`} />
          </label>
          {WEEKLY_FIELDS.map(([key, label]) => (
            <label key={key} className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">{label}</span>
              <input type="number" min="0" value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: Number(e.target.value) })}
                data-testid={`harvest-kpi-input-${key}`} className={`${inputClass} mt-1.5`} />
            </label>
          ))}
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">Spend line</span>
            <input value={spendLine.name} onChange={(e) => setSpendLine({ ...spendLine, name: e.target.value })}
              placeholder="e.g. postage" data-testid="harvest-kpi-spend-name"
              className={`${inputClass} mt-1.5`} />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">Amount</span>
            <input type="number" min="0" step="0.01" value={spendLine.amount}
              onChange={(e) => setSpendLine({ ...spendLine, amount: e.target.value })}
              data-testid="harvest-kpi-spend-amount" className={`${inputClass} mt-1.5`} />
          </label>
        </div>
        <button type="button" onClick={save} className={`${buttonClass} mt-5`} data-testid="harvest-kpi-save">
          Save this week
        </button>
      </Panel>

      <Panel title="Per package" testid="harvest-kpi-packages">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
                {["Package", "Sent", "Scans", "Cost", "Cost per meeting"].map((h) => (
                  <th key={h} className="pb-3 pr-4 font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.by_package || []).map((row) => (
                <tr key={row.package_key} className="border-t border-cream/5"
                  data-testid={`harvest-kpi-package-${row.package_key}`}>
                  <td className="py-3 pr-4 text-cream">{row.package_key}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-cream/70">{row.sent}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-cream/70">{row.scans}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-cream/70">${row.cost.toFixed(2)}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-cream/70">
                    {totals.meetings ? `$${(row.cost / totals.meetings).toFixed(2)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!data?.by_package?.length ? (
            <p className="py-6 text-sm text-cream/40">Nothing shipped or scanned yet.</p>
          ) : null}
        </div>
      </Panel>
    </HarvestShell>
  );
}
